/**
 * @packageDocumentation
 * @module f1/is
 */

interface TypeWithArgs<T, A extends unknown[]> extends Function {
  new (...args: A): T;
}

export interface IsClass {
  /**
   * Is the object a class
   *
   * @public
   * @function f1.is.class
   * @example
   *
   * ```ts
   * class C {}
   * f1.is.class(C) => true
   *
   * function F {}
   * f1.is.class(F) => true
   *
   * const n1 = new Number(1)
   * f1.is.class(n1) => false
   * ```
   */
  <T, A extends unknown[]>(value: unknown): value is TypeWithArgs<T, A>;
}

/**
 * @see IsClass
 */
export default <T, A extends unknown[]>(value: unknown): value is TypeWithArgs<T, A> => {
  if (!value) {
    return false;
  }

  const typeOf = typeof value;
  if (typeOf === 'function') {
    return true;
  }

  return (
    typeOf === 'object' &&
    (value as Object).constructor &&
    (value as Object).constructor.toString().indexOf('class') >= 0
  );
};
